import { create } from "zustand";
import { createJSONStorage, persist, type StateStorage } from "zustand/middleware";

type AuthState = {
  token: string | null;
  refreshToken: string | null;
  rememberMe: boolean;
  setSession: (p: {
    accessToken: string;
    refreshToken: string;
    rememberMe: boolean;
  }) => void;
  logout: () => void;
};

/** Remember-me → localStorage; otherwise sessionStorage (cleared when the tab closes). */
const hybridStorage: StateStorage = {
  getItem: (name) => {
    if (typeof window === "undefined") return null;
    return localStorage.getItem(name) ?? sessionStorage.getItem(name);
  },
  setItem: (name, value) => {
    if (typeof window === "undefined") return;
    try {
      const o = JSON.parse(value) as { state?: { rememberMe?: boolean } };
      const remember = o?.state?.rememberMe === true;
      if (remember) {
        localStorage.setItem(name, value);
        sessionStorage.removeItem(name);
      } else {
        sessionStorage.setItem(name, value);
        localStorage.removeItem(name);
      }
    } catch {
      sessionStorage.setItem(name, value);
      localStorage.removeItem(name);
    }
  },
  removeItem: (name) => {
    if (typeof window === "undefined") return;
    localStorage.removeItem(name);
    sessionStorage.removeItem(name);
  },
};

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      rememberMe: false,
      setSession: ({ accessToken, refreshToken, rememberMe }) =>
        set({
          token: accessToken,
          refreshToken,
          rememberMe,
        }),
      logout: () => set({ token: null, refreshToken: null, rememberMe: false }),
    }),
    {
      name: "xmegami-admin-auth",
      storage: createJSONStorage(() => hybridStorage),
      partialize: (s) => ({
        token: s.token,
        refreshToken: s.refreshToken,
        rememberMe: s.rememberMe,
      }),
    }
  )
);
